// ─────────────────────────────────────────────────────────────────────────────
// 🔑 THE "SENT WITH THE APPLICATION" ICON (LO plan v7, items 5 and 9 — Jesse, 2026-10-28).
//
// One small mark beside a document that arrived with the application, in place of words. The words are on the
// mark itself: aria-label, a tooltip on hover, and the same tooltip on tap (a phone has no hover).
//   · borrower (guided step 5): label "Sent with your application" — never a person, role or firm;
//   · staff (Underwriting Module, All documents): label "Sent by <name>, <firm>".
// Brand colours only (tailwind doorly-*).
// ─────────────────────────────────────────────────────────────────────────────
import React, { useEffect, useRef, useState } from 'react';

export const BORROWER_SENT_LABEL = 'Sent with your application';

export default function SentIcon({ label = BORROWER_SENT_LABEL, testId = 'sent-icon' }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  // A tap elsewhere closes a tapped-open tooltip.
  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('pointerdown', close);
    return () => document.removeEventListener('pointerdown', close);
  }, [open]);
  // 🚨 A CLICK OPENS, IT NEVER TOGGLES (2026-10-30). Hover opened it and the click that followed toggled it shut, so on
  // desktop a click hid the tooltip it had just shown; on a phone the tap's emulated mouseenter/mouseleave did the same.
  // Hover now listens to a real mouse only (pointerType), a click/tap always opens, and a tap elsewhere (or the mouse
  // leaving) closes it. E2E scenario 21 caught it as a timing-dependent failure.
  const mouseOnly = (fn) => (e) => { if (e.pointerType === 'mouse') fn(); };
  return (
    <span ref={ref} className="relative inline-flex align-middle"
      onPointerEnter={mouseOnly(() => setOpen(true))} onPointerLeave={mouseOnly(() => setOpen(false))}>
      <span role="img" aria-label={label} title={label} data-testid={testId} data-label={label} tabIndex={0}
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpen((o) => !o); } }}
        className="inline-grid place-items-center w-[18px] h-[18px] rounded-[5px] bg-doorly-neutral-lightest text-doorly-blue cursor-pointer flex-shrink-0">
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" aria-hidden="true">
          <path d="M14 3H6v18h12V7z" /><path d="M9 14h6M12 11l3 3-3 3" />
        </svg>
      </span>
      {open && (
        <span role="tooltip" data-testid={`${testId}-tip`}
          className="absolute left-0 bottom-full mb-1 z-20 whitespace-nowrap rounded-md bg-doorly-navy px-2 py-1 text-[11.5px] font-semibold text-white">
          {label}
        </span>
      )}
    </span>
  );
}
